import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { sessionExpired } from '@/features/auth/authSlice';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE ?? '/api/v1',
  // The session rides on an httpOnly cookie, so every request must carry it.
  // Nothing sensitive is ever placed in localStorage.
  credentials: 'include',
});

/**
 * Wraps the base query so that a 401 anywhere in the app clears local auth
 * state once, rather than each feature having to notice for itself.
 */
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    api.dispatch(sessionExpired());
  }

  return result;
};

/**
 * One API instance for the whole app. Features attach their own endpoints via
 * `injectEndpoints`, so a lazily-loaded feature brings its endpoints with it
 * instead of everything being defined up front.
 */
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Session',
    'Employee',
    'Directory',
    'Department',
    'JobTitle',
    'Invitation',
    'Leave',
    'LeaveType',
    'Sickness',
    'ProfileChange',
    'Document',
    'ManagedDocument',
    'PayrollBatch',
    'Handbook',
    'Policy',
    'Contract',
    'Pack',
    'Onboarding',
    'Benefit',
    'BenefitRequest',
    'Purchase',
    'Course',
    'Enrolment',
    'Thread',
    'Notification',
    'Audit',
    'Settings',
    'CsvImport',
  ],
  endpoints: () => ({}),
});

/** Narrows an unknown RTK Query error into something displayable. */
export function errorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const data = (error as { data?: unknown }).data;
    if (typeof data === 'object' && data !== null && 'error' in data) {
      const inner = (data as { error?: { message?: string } }).error;
      if (inner?.message) return inner.message;
    }
  }
  return fallback;
}
